import React from "react";
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import AdBanner from '../components/AdBanner';
import HilltopBanner from '../components/HilltopBanner';

const FOCUS_AREAS = [
  {
    icon: 'fitness_center',
    title: 'Health & Nutrition',
    desc: 'Calorie and BMI calculators, sleep optimization tools, and evidence-based nutritional guides for peak performance.',
  },
  {
    icon: 'eco',
    title: 'Sustainability',
    desc: 'Carbon footprint compute engines and interactive protocols to help you meet your environmental targets.',
  },
  {
    icon: 'savings',
    title: 'Personal Finance',
    desc: 'Budget planning modules and financial clarity frameworks — precision-engineered for smart decisions.',
  },
];

const VALUES = [
  { code: '01', key: 'CLARITY', value: 'Plain-language explanations with clean, distraction-free interfaces.' },
  { code: '02', key: 'ACCURACY', value: 'Evidence-based content and verified computation logic you can trust.' },
  { code: '03', key: 'ACCESSIBILITY', value: 'Tools that work for everyone — no jargon, no barriers.' },
  { code: '04', key: 'PERFORMANCE', value: 'Engines built for speed, responsiveness, and zero-latency output.' },
];

export default function AboutUs() {
  return (
    <div className="min-h-screen">
      <SEO
        title="CalcBlog - About the System"
        description="CalcBlog engineers precision calculation tools and data-driven articles for health, sustainability, and personal finance."
        keywords="about calcblog, interactive calculators, evidence-based tools, data clarity"
        toolType="blog"
      />

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col lg:flex-row gap-8 py-20">

        {/* Left Ad Column */}
        <aside className="hidden lg:flex w-[160px] shrink-0 justify-center">
          <div className="sticky top-24 h-fit">
            <AdBanner adKey="f944541495caabb1dbef72e5e10873f3" width={160} height={600} instanceId="ad7" />
          </div>
        </aside>

        {/* Main Content */}
        <main aria-labelledby="aboutus-title" className="flex-1 min-w-0 space-y-24">

          {/* Hero */}
          <section className="space-y-6">
            <h1
              id="aboutus-title"
              className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1]"
            >
              ABOUT CALCBLOG
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl leading-relaxed">
              We engineer precision-grade interactive calculators and intelligence reports that transform complex data into actionable clarity, so anyone can make smart, confident decisions.
            </p>
          </section>

          {/* Divider */}
          <div className="border-t border-white/5" />

          {/* Mission + Vision */}
          <section aria-labelledby="mission-vision-title" className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="glass rounded-2xl p-8 space-y-4 border-l-4 border-l-brand">
              <h2 id="mission-vision-title" className="text-2xl font-bold text-white">OUR MISSION</h2>
              <p className="text-slate-400 leading-relaxed">
                Make education practical. CalcBlog blends precision engineering with accessible design to deliver tools that help you measure, plan, and improve — without the jargon.
              </p>
            </div>
            <div className="glass rounded-2xl p-8 space-y-4 border-l-4 border-l-brand/50">
              <h2 className="text-2xl font-bold text-white">OUR VISION</h2>
              <p className="text-slate-400 leading-relaxed">
                A world where anyone can explore health, sustainability, and finance through interactive compute engines that are accurate, accessible, and genuinely delightful.
              </p>
            </div>
          </section>

          {/* Focus Areas */}
          <section aria-labelledby="focus-areas-title">
            <div className="mb-12 space-y-2">
              <h2 id="focus-areas-title" className="text-3xl font-bold text-white tracking-tight">Our Focus Areas</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {FOCUS_AREAS.map((area, idx) => (
                <div key={idx} className="glass rounded-2xl p-8 space-y-4 group hover:border-brand/50 transition-colors">
                  <div className="w-12 h-12 rounded-xl glass flex items-center justify-center group-hover:bg-brand/20 transition-colors border border-brand/20">
                    <span className="material-symbols-outlined text-brand">{area.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white group-hover:text-brand transition-colors">{area.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{area.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Ad Banner */}
          <div className="flex justify-center py-4 border-y border-white/5">
            <HilltopBanner />
          </div>

          {/* Values */}
          <section aria-labelledby="values-title">
            <div className="mb-12 space-y-2">
              <h2 id="values-title" className="text-3xl font-bold text-white tracking-tight">What we Value</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {VALUES.map((v) => (
                <div key={v.code} className="glass rounded-2xl p-8 flex gap-6">
                  <span className="font-mono text-[10px] text-slate-600 tracking-widest shrink-0 pt-1">{v.code}</span>
                  <div>
                    <p className="text-xs text-brand font-bold tracking-widest mb-2 uppercase">{v.key}</p>
                    <p className="text-slate-400 leading-relaxed">{v.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Story */}
          <section aria-labelledby="story-title" className="glass rounded-2xl p-8 md:p-12 relative overflow-hidden border-brand/20">
            <div className="relative z-10 space-y-4">
              <h2 id="story-title" className="text-3xl font-bold text-white tracking-tight">Our Story</h2>
              <p className="text-slate-400 max-w-2xl leading-relaxed">
                CalcBlog began as a passion project with one directive: build tools that bridge elegant design with real-world utility. What started as a few calculators grew into a full intelligence platform, always guided by the principle that clarity is a feature, not an afterthought.
              </p>
              <Link to="/calculators" className="inline-flex items-center gap-2 mt-4 bg-brand text-[#050805] px-8 py-3 rounded-full font-bold hover:scale-105 transition-transform glow-green">
                EXPLORE CALCULATORS
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </Link>
            </div>
            <div className="absolute top-0 right-0 w-64 h-full bg-grid-pattern opacity-10 pointer-events-none"></div>
          </section>

          {/* Footer Note */}
          <p className="font-mono text-[10px] text-slate-600 tracking-[0.2em] text-center uppercase">
            © {new Date().getFullYear()} CalcBlog — Engineered for Performance. Driven by Clarity.
          </p>

        </main>

        {/* Right Ad Column */}
        <aside className="w-full lg:w-[160px] shrink-0 flex justify-center">
          <div className="lg:sticky lg:top-24 h-fit" />
        </aside>

      </div>
    </div>
  );
}